import React, { useState } from "react";
import Card from "components/card";
import { FormattedQuestionResponse } from "services/questionnaireService";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdUnfoldLess,
  MdUnfoldMore,
} from "react-icons/md";

interface QuestionnaireResponsesProps {
  responses: Record<string, Record<string, FormattedQuestionResponse>>;
  questionnaireType?: string; // Optional type of questionnaire (e.g., "OSI", "STAI", "BECK")
}

const QuestionnaireResponses: React.FC<QuestionnaireResponsesProps> = ({
  responses,
  questionnaireType,
}) => {
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  // State to track which sections show all questions vs limited questions
  const [showMoreMap, setShowMoreMap] = useState<Record<string, boolean>>({});
  // Number of questions to show initially
  const INITIAL_QUESTIONS_TO_SHOW = 10;

  // All section IDs
  const sectionIds = Object.keys(responses);

  // Check if all sections are expanded
  const allExpanded =
    sectionIds.length > 0 && sectionIds.every((id) => expandedSections[id]);

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Toggle show more/less questions for a section
  const toggleShowMore = (sectionId: string, event: React.MouseEvent) => {
    // Prevent the click from toggling the section expansion
    event.stopPropagation();

    setShowMoreMap((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Toggle all sections
  const toggleAllSections = () => {
    const newState = !allExpanded;
    const updatedSections = sectionIds.reduce((acc, sectionId) => {
      acc[sectionId] = newState;
      return acc;
    }, {} as Record<string, boolean>);

    setExpandedSections(updatedSections);
  };

  // Format the response based on type and questionnaire type
  const formatResponse = (answer: any): JSX.Element => {
    if (answer === null || answer === undefined) {
      return <span className="italic text-gray-500">No respondido</span>;
    }

    // Special handling for OSI questionnaire
    if (questionnaireType === "OSI" && typeof answer === "string") {
      const osiValues: Record<string, { text: string; color: string }> = {
        NUNCA: { text: "Nunca", color: "text-red-600" },
        ALGUNAS_VECES: { text: "Algunas veces", color: "text-orange-600" },
        A_MENUDO: { text: "A menudo", color: "text-yellow-600" },
        MUY_A_MENUDO: { text: "Muy a menudo", color: "text-green-600" },
        SIEMPRE: { text: "Siempre", color: "text-green-700 font-bold" },
      };

      if (osiValues[answer]) {
        return (
          <span className={`${osiValues[answer].color} rounded px-2 py-1`}>
            {osiValues[answer].text}
          </span>
        );
      }
    }

    // Special handling for IVA questionnaire
    if (questionnaireType === "IVA" && typeof answer === "number") {
      const ivaValues: Record<
        number,
        { text: string; color: string; bgColor: string }
      > = {
        1: { text: "Casi Nunca", color: "text-red-600", bgColor: "bg-red-50" },
        2: {
          text: "Pocas Veces",
          color: "text-orange-600",
          bgColor: "bg-orange-50",
        },
        3: {
          text: "Unas veces sí, Otras veces no",
          color: "text-yellow-600",
          bgColor: "bg-yellow-50",
        },
        4: {
          text: "Muchas Veces",
          color: "text-blue-600",
          bgColor: "bg-blue-50",
        },
        5: {
          text: "Casi Siempre",
          color: "text-green-600",
          bgColor: "bg-green-50",
        },
      };

      if (ivaValues[answer]) {
        return (
          <span
            className={`${ivaValues[answer].color} ${ivaValues[answer].bgColor} inline-block rounded-md px-3 py-1.5 font-medium`}
          >
            {ivaValues[answer].text}
          </span>
        );
      }
    }

    if (typeof answer === "object") {
      if (Array.isArray(answer)) {
        return (
          <ul className="list-inside list-disc">
            {answer.map((item, index) => (
              <li key={index} className="mt-1">
                {typeof item === "object" ? JSON.stringify(item) : String(item)}
              </li>
            ))}
          </ul>
        );
      }

      return (
        <div className="mt-1 rounded bg-gray-100 p-2 font-mono text-xs dark:bg-navy-900">
          {JSON.stringify(answer, null, 2)}
        </div>
      );
    }

    // Handle boolean values
    if (typeof answer === "boolean") {
      return (
        <span className={answer ? "text-green-600" : "text-red-600"}>
          {answer ? "Sí" : "No"}
        </span>
      );
    }

    // Handle numeric values
    if (typeof answer === "number") {
      return <span className="font-semibold">{answer}</span>;
    }

    // Default for strings
    return <span>{String(answer)}</span>;
  };

  // Format section title for display
  const formatSectionTitle = (sectionId: string): string => {
    // Replace underscores with spaces and capitalize
    return sectionId
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <Card extra="w-full p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-2">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
          Respuestas
        </h2>

        <button
          onClick={toggleAllSections}
          className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-200 dark:hover:bg-navy-600"
        >
          {allExpanded ? (
            <>
              <MdUnfoldLess className="h-5 w-5" />
              <span>Colapsar Todo</span>
            </>
          ) : (
            <>
              <MdUnfoldMore className="h-5 w-5" />
              <span>Expandir Todo</span>
            </>
          )}
        </button>
      </div>

      {questionnaireType && (
        <div className="mb-4">
          <div className="inline-block rounded-md bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-navy-800 dark:text-blue-400">
            Tipo de cuestionario: {questionnaireType}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(responses).map(([sectionId, sectionResponses]) => {
          const isExpanded = expandedSections[sectionId] || false;
          const questionCount = Object.keys(sectionResponses).length;

          return (
            <div
              key={sectionId}
              className="overflow-hidden rounded-lg border border-gray-200 shadow-sm"
            >
              {/* Section header - clickable */}
              <button
                onClick={() => toggleSection(sectionId)}
                className="flex w-full items-center justify-between bg-gray-50 p-4 text-left transition-colors hover:bg-gray-100 dark:bg-navy-700 dark:hover:bg-navy-600"
                aria-expanded={isExpanded}
              >
                <div>
                  <h3 className="text-lg font-semibold capitalize text-navy-700 dark:text-white">
                    {formatSectionTitle(sectionId)}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {questionCount}{" "}
                    {questionCount === 1 ? "pregunta" : "preguntas"}
                  </p>
                </div>
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 transition-transform duration-300 dark:bg-navy-600 ${
                    isExpanded ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <MdKeyboardArrowDown className="h-6 w-6 text-gray-600 dark:text-white" />
                </div>
              </button>

              {/* Section content - expandable */}
              <div
                className={`border-t border-gray-200 transition-all duration-300 ease-in-out
                  ${isExpanded ? "block" : "hidden"}
                `}
              >
                <div className="p-4">
                  <div className="space-y-4">
                    {Object.entries(sectionResponses).map(
                      ([questionId, response], index) => {
                        const showAllQuestions =
                          showMoreMap[sectionId] || false;
                        const questionCount =
                          Object.keys(sectionResponses).length;
                        const shouldShowQuestion =
                          showAllQuestions || index < INITIAL_QUESTIONS_TO_SHOW;

                        if (!shouldShowQuestion) return null;

                        return (
                          <div
                            key={questionId}
                            className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800"
                          >
                            <div className="mb-2 flex items-start">
                              <span className="mr-2 rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-navy-700 dark:text-gray-300">
                                {questionId}
                              </span>
                              <p className="font-medium text-navy-700 dark:text-white">
                                {response.question}
                              </p>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <span className="font-medium text-brand-500 dark:text-brand-400">
                                Respuesta:
                              </span>
                              <div className="mt-1 flex items-center">
                                {formatResponse(response.answer)}
                              </div>
                            </div>
                          </div>
                        );
                      }
                    )}

                    {/* Show more/less button for sections with more than the initial number of questions */}
                    {Object.keys(sectionResponses).length >
                      INITIAL_QUESTIONS_TO_SHOW && (
                      <div className="mt-4 flex justify-center">
                        <button
                          onClick={(e) => toggleShowMore(sectionId, e)}
                          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                        >
                          {showMoreMap[sectionId]
                            ? "Mostrar menos preguntas"
                            : `Mostrar ${
                                Object.keys(sectionResponses).length -
                                INITIAL_QUESTIONS_TO_SHOW
                              } más`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {Object.keys(responses).length === 0 && (
        <div className="rounded-lg bg-gray-50 p-4 text-center text-gray-500 dark:bg-navy-700 dark:text-gray-400">
          No hay respuestas disponibles para este cuestionario.
        </div>
      )}

      {/* Legend for OSI responses */}
      {questionnaireType === "OSI" && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:bg-navy-800">
          <h3 className="mb-2 font-semibold text-navy-700 dark:text-white">
            Leyenda de respuestas
          </h3>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1">
              <span className="text-red-600">Nunca</span>
              <span className="text-gray-500">-</span>
              <span className="text-orange-600">Algunas veces</span>
              <span className="text-gray-500">-</span>
              <span className="text-yellow-600">A menudo</span>
              <span className="text-gray-500">-</span>
              <span className="text-green-600">Muy a menudo</span>
              <span className="text-gray-500">-</span>
              <span className="font-bold text-green-700">Siempre</span>
            </div>
          </div>
        </div>
      )}

      {/* Legend for IVA responses */}
      {questionnaireType === "IVA" && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:bg-navy-800">
          <h3 className="mb-2 font-semibold text-navy-700 dark:text-white">
            Leyenda de respuestas
          </h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            <div className="rounded bg-red-50 px-3 py-2">
              <span className="text-sm font-medium text-red-600">
                1 - Casi Nunca
              </span>
            </div>
            <div className="rounded bg-orange-50 px-3 py-2">
              <span className="text-sm font-medium text-orange-600">
                2 - Pocas Veces
              </span>
            </div>
            <div className="rounded bg-yellow-50 px-3 py-2">
              <span className="text-sm font-medium text-yellow-600">
                3 - Unas veces sí, Otras veces no
              </span>
            </div>
            <div className="rounded bg-blue-50 px-3 py-2">
              <span className="text-sm font-medium text-blue-600">
                4 - Muchas Veces
              </span>
            </div>
            <div className="rounded bg-green-50 px-3 py-2">
              <span className="text-sm font-medium text-green-600">
                5 - Casi Siempre
              </span>
            </div>
          </div>
        </div>
      )}

    </Card>
  );
};

export default QuestionnaireResponses;
